import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { GitMerge, Loader2, Search, X } from "lucide-react";
import { toast } from "sonner";
import { useCustomers, useMergeVisitor } from "../lib/customers";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: number;
  /** The visitor everything gets merged INTO (the page the dialog opened on). */
  target: { visitorId: number; label: string };
}

/**
 * "Merge with" dialog on the customer detail page: search another visitor by
 * name / email / phone (same search as the customers list) and merge it into
 * the current one. The selected visitor's events and keys move over; its
 * profile is soft-deleted (merged_into).
 */
export function MergeVisitorDialog({
  open,
  onOpenChange,
  projectId,
  target,
}: Props) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selected, setSelected] = useState<{
    visitorId: number;
    label: string;
  } | null>(null);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setDebouncedSearch("");
    setSelected(null);
  }, [open]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const searchQuery = useCustomers({
    projectId:
      open && !selected && debouncedSearch.length >= 2 ? projectId : null,
    q: debouncedSearch || undefined,
    limit: 8,
    offset: 0,
  });
  const results = (searchQuery.data?.items ?? []).filter(
    (c) => c.visitorId !== target.visitorId,
  );

  const merge = useMergeVisitor(projectId);

  const submit = () => {
    if (!selected || merge.isPending) return;
    merge.mutate(
      {
        targetVisitorId: target.visitorId,
        sourceVisitorId: selected.visitorId,
      },
      {
        onSuccess: () => {
          toast.success(
            `Visitor ${selected.visitorId} wurde in ${target.visitorId} gemergt — Events und Keys sind übernommen.`,
          );
          onOpenChange(false);
        },
        onError: (err) =>
          toast.error(`Merge fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-overlay bg-overlay animate-fade-in" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-modal w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-md border border-border-primary bg-bg-elevated shadow-overlay animate-fade-in">
          <div className="flex items-center justify-between border-b border-border-primary px-5 py-3">
            <Dialog.Title className="font-medium text-text-primary">
              Merge with
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Schließen"
                className="text-text-tertiary hover:text-text-primary"
              >
                <X size={16} />
              </button>
            </Dialog.Close>
          </div>

          <div className="space-y-4 px-5 py-4">
            <p className="text-xs text-text-tertiary">
              Suche den Visitor, der in{" "}
              <span className="font-medium text-text-primary">
                {target.label}
              </span>{" "}
              <span className="font-mono">(id {target.visitorId})</span>{" "}
              gemergt werden soll. Dessen Events und Keys werden übernommen.
            </p>

            {selected ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 rounded-md border border-border-primary bg-bg-secondary px-3 py-2">
                  <span className="flex-1 truncate text-sm text-text-primary">
                    {selected.label}
                  </span>
                  <span className="font-mono text-[10px] text-text-tertiary">
                    id {selected.visitorId}
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelected(null)}
                    aria-label="Visitor abwählen"
                    className="text-text-tertiary hover:text-danger"
                  >
                    <X size={13} />
                  </button>
                </div>
                <div className="rounded-md border border-warning bg-warning-bg px-3 py-2 text-xs text-warning">
                  <span className="font-mono">{selected.visitorId}</span> →{" "}
                  <span className="font-mono">{target.visitorId}</span>: Der
                  ausgewählte Visitor wird dauerhaft in diesen zusammengeführt.
                  Das ist nicht umkehrbar.
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-2 rounded-md border border-border-primary bg-bg-primary px-3 py-2">
                  <Search size={13} className="shrink-0 text-text-tertiary" />
                  <input
                    type="search"
                    autoFocus
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Name, Email oder Telefon suchen…"
                    className="flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none"
                  />
                  {searchQuery.isFetching && (
                    <Loader2
                      size={13}
                      className="animate-spin text-text-tertiary"
                    />
                  )}
                </div>
                {debouncedSearch.length >= 2 && searchQuery.data && (
                  <ul className="mt-1 max-h-48 overflow-auto rounded-md border border-border-primary bg-bg-primary">
                    {results.length === 0 ? (
                      <li className="px-3 py-2 text-xs text-text-tertiary">
                        Kein anderer Visitor gefunden — suche nach Name,
                        Email-Adresse oder Telefonnummer.
                      </li>
                    ) : (
                      results.map((c) => (
                        <li key={c.visitorId}>
                          <button
                            type="button"
                            onClick={() =>
                              setSelected({
                                visitorId: c.visitorId,
                                label: c.displayName,
                              })
                            }
                            className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-bg-hover"
                          >
                            <span className="flex-1 truncate text-sm text-text-primary">
                              {c.displayName}
                            </span>
                            <span className="shrink-0 text-[10px] text-text-tertiary">
                              {c.lastEventType} ·{" "}
                              {new Date(c.lastActionAt).toLocaleDateString(
                                "de-DE",
                              )}
                            </span>
                            <span className="shrink-0 font-mono text-[10px] text-text-tertiary">
                              id {c.visitorId}
                            </span>
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-border-primary bg-bg-secondary px-5 py-3">
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-md border border-border-primary bg-bg-primary px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-bg-hover"
              >
                Abbrechen
              </button>
            </Dialog.Close>
            <button
              type="button"
              onClick={submit}
              disabled={!selected || merge.isPending}
              className="inline-flex items-center gap-1 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-text-on-accent hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {merge.isPending ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <GitMerge size={13} />
              )}
              Zusammenführen
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
